import { Controller } from "@hotwired/stimulus"
import { currencySymbol } from "../currency.js"

export default class extends Controller {
  static targets = [
    // Tier toggle
    "tierBtn",
    // Member inputs
    "memberCount", "memberSlider",
    // Revenue sliders + hidden inputs + displays
    "greenFeeSlider", "greenFeeRevenue", "greenFeeDisplay",
    "eventSlider", "eventRevenue", "eventDisplay",
    "facilitySlider", "facilityRevenue", "facilityDisplay",
    "posSlider", "posRevenue", "posDisplay",
    "golfnowSlider", "golfnowRevenue", "golfnowDisplay",
    "giftCardSlider", "giftCardRevenue", "giftCardDisplay",
    // Usage sliders + hidden inputs + displays
    "emailSlider", "emailCount", "emailDisplay",
    "accessSlider", "accessCount", "accessDisplay",
    "smsSlider", "smsCount", "smsDisplay",
    // Rate overrides
    "minimumFee",
    "bandRate1", "bandRate2", "bandRate3", "bandRate4", "bandRate5",
    "transactionRate", "golfnowRate", "giftCardRate",
    "emailFreeLimit", "emailRate1", "emailRate2", "emailThreshold",
    "accessFreeLimit", "accessRate1", "accessRate2", "accessThreshold",
    "emailFreeDisplay", "accessFreeDisplay",
    "smsRate",
    // Outputs
    "baseFeeMonthly", "baseFeeAnnual", "baseFeeBreakdown",
    "transactionFeeMonthly", "transactionFeeAnnual", "transactionBreakdown",
    "marketplaceFeeMonthly", "marketplaceFeeAnnual",
    "giftCardFeeMonthly", "giftCardFeeAnnual",
    "usageFeeMonthly", "usageFeeAnnual", "usageBreakdown",
    "totalMonthly", "totalAnnual", "totalQuarterly",
    "effectivePerMember",
    "ratesPanel",
    // ROI
    "hoursSlider", "hoursDisplay",
    "roiCostBreakdown", "roiHoursSaved", "roiValueAnnual", "roiCostAnnual", "roiMultiple"
  ]

  static ADMIN_REDUCTION = 0.8
  static HOURLY_RATE = 20

  static TIER_DEFAULTS = {
    teesheet: { min: 150, rates: [0.70, 0.30, 0.22, 0.15, 0.10] },
    platform:  { min: 250, rates: [2.30, 1.00, 0.85, 0.60, 0.40] }
  }

  connect() {
    this.currentTier = "platform"
    this.calculate()
  }

  setTier(event) {
    this.currentTier = event.currentTarget.dataset.tier
    const defaults = this.constructor.TIER_DEFAULTS[this.currentTier]
    this.minimumFeeTarget.value = defaults.min
    this.bandRate1Target.value = defaults.rates[0]
    this.bandRate2Target.value = defaults.rates[1]
    this.bandRate3Target.value = defaults.rates[2]
    this.bandRate4Target.value = defaults.rates[3]
    this.bandRate5Target.value = defaults.rates[4]
    this.tierBtnTargets.forEach(btn => {
      const active = btn.dataset.tier === this.currentTier
      btn.classList.toggle("bg-green-700", active)
      btn.classList.toggle("text-white", active)
      btn.classList.toggle("bg-white", !active)
      btn.classList.toggle("text-gray-700", !active)
    })
    this.calculate()
  }

  // Sync revenue sliders → hidden inputs → displays, then recalculate
  syncRevenue() {
    const pairs = [
      [this.greenFeeSliderTarget, this.greenFeeRevenueTarget, this.greenFeeDisplayTarget],
      [this.eventSliderTarget, this.eventRevenueTarget, this.eventDisplayTarget],
      [this.facilitySliderTarget, this.facilityRevenueTarget, this.facilityDisplayTarget],
      [this.posSliderTarget, this.posRevenueTarget, this.posDisplayTarget],
      [this.golfnowSliderTarget, this.golfnowRevenueTarget, this.golfnowDisplayTarget],
      [this.giftCardSliderTarget, this.giftCardRevenueTarget, this.giftCardDisplayTarget]
    ]
    for (const [slider, hidden, display] of pairs) {
      hidden.value = slider.value
      display.textContent = parseInt(slider.value).toLocaleString("en-IE")
    }
    this.calculate()
  }

  // Sync usage sliders → hidden inputs → displays, then recalculate
  syncUsage() {
    const pairs = [
      [this.emailSliderTarget, this.emailCountTarget, this.emailDisplayTarget],
      [this.accessSliderTarget, this.accessCountTarget, this.accessDisplayTarget],
      [this.smsSliderTarget, this.smsCountTarget, this.smsDisplayTarget]
    ]
    for (const [slider, hidden, display] of pairs) {
      hidden.value = slider.value
      display.textContent = parseInt(slider.value).toLocaleString("en-IE")
    }
    this.calculate()
  }

  // Sync member slider → number input
  syncSlider() {
    this.memberCountTarget.value = this.memberSliderTarget.value
    this.calculate()
  }

  calculate() {
    const members = this.intVal(this.memberCountTarget)
    this.memberSliderTarget.value = members

    // Base fee: progressive bands from member 1, minimum monthly floor
    const minimumFee = this.floatVal(this.minimumFeeTarget)
    const bandRates = [
      { limit: 100,      rate: this.floatVal(this.bandRate1Target) },
      { limit: 250,      rate: this.floatVal(this.bandRate2Target) },
      { limit: 500,      rate: this.floatVal(this.bandRate3Target) },
      { limit: 1000,     rate: this.floatVal(this.bandRate4Target) },
      { limit: Infinity, rate: this.floatVal(this.bandRate5Target) }
    ]

    let usageMonthly = 0
    let breakdownParts = []
    let prev = 0

    for (const band of bandRates) {
      if (members <= prev) break
      const inBand = Math.min(members, band.limit) - prev
      if (inBand <= 0) break
      const cost = inBand * band.rate
      usageMonthly += cost
      breakdownParts.push(
        `Members ${prev + 1}–${Math.min(members, band.limit)} × ${currencySymbol()}${band.rate.toFixed(2)}/mo = ${currencySymbol()}${cost.toFixed(2)}/mo`
      )
      prev = band.limit
    }

    const baseFeeMonthly = members > 0 ? Math.max(minimumFee, usageMonthly) : 0
    const baseFeeAnnual = baseFeeMonthly * 12
    const minApplied = members > 0 && usageMonthly < minimumFee

    this.baseFeeMonthlyTarget.textContent = this.formatCurrency(baseFeeMonthly)
    this.baseFeeAnnualTarget.textContent = this.formatCurrency(baseFeeAnnual)
    this.baseFeeBreakdownTarget.innerHTML =
      (breakdownParts.join("<br>") || "No members entered") +
      (minApplied ? `<br><span class="text-amber-700">Min ${currencySymbol()}${minimumFee.toFixed(0)}/mo applied</span>` : "")

    // Transaction fees (monthly revenue inputs × rate)
    const txRate = this.floatVal(this.transactionRateTarget) / 100
    const greenFee = this.floatVal(this.greenFeeRevenueTarget) * txRate
    const eventFee = this.floatVal(this.eventRevenueTarget) * txRate
    const facilityFee = this.floatVal(this.facilityRevenueTarget) * txRate
    const posFee = this.floatVal(this.posRevenueTarget) * txRate
    const totalTxFee = greenFee + eventFee + facilityFee + posFee

    this.transactionFeeMonthlyTarget.textContent = this.formatCurrency(totalTxFee)
    this.transactionFeeAnnualTarget.textContent = this.formatCurrency(totalTxFee * 12)

    let txParts = []
    if (greenFee > 0) txParts.push(`Green fees: ${currencySymbol()}${greenFee.toFixed(2)}`)
    if (eventFee > 0) txParts.push(`Events: ${currencySymbol()}${eventFee.toFixed(2)}`)
    if (facilityFee > 0) txParts.push(`Facilities: ${currencySymbol()}${facilityFee.toFixed(2)}`)
    if (posFee > 0) txParts.push(`POS: ${currencySymbol()}${posFee.toFixed(2)}`)
    this.transactionBreakdownTarget.innerHTML = txParts.join("<br>") || "No revenue entered"

    // Marketplace fees
    const golfnowRate = this.floatVal(this.golfnowRateTarget) / 100
    const golfnowFee = this.floatVal(this.golfnowRevenueTarget) * golfnowRate
    this.marketplaceFeeMonthlyTarget.textContent = this.formatCurrency(golfnowFee)
    this.marketplaceFeeAnnualTarget.textContent = this.formatCurrency(golfnowFee * 12)

    // Gift card fees
    const giftCardRate = this.floatVal(this.giftCardRateTarget) / 100
    const giftCardFee = this.floatVal(this.giftCardRevenueTarget) * giftCardRate
    this.giftCardFeeMonthlyTarget.textContent = this.formatCurrency(giftCardFee)
    this.giftCardFeeAnnualTarget.textContent = this.formatCurrency(giftCardFee * 12)

    // Usage-based fees (flat free tiers)
    const emailFreeLimit = this.intVal(this.emailFreeLimitTarget)
    this.emailFreeDisplayTarget.textContent = emailFreeLimit.toLocaleString("en-IE")

    const emailCount = this.intVal(this.emailCountTarget)
    const emailThreshold = this.intVal(this.emailThresholdTarget)
    const emailRate1 = this.floatVal(this.emailRate1Target)
    const emailRate2 = this.floatVal(this.emailRate2Target)
    const emailFee = this.calculateUsageFee(emailCount, emailFreeLimit, emailThreshold, emailRate1, emailRate2)

    const accessFreeLimit = this.intVal(this.accessFreeLimitTarget)
    this.accessFreeDisplayTarget.textContent = accessFreeLimit.toLocaleString("en-IE")

    const accessCount = this.intVal(this.accessCountTarget)
    const accessThreshold = this.intVal(this.accessThresholdTarget)
    const accessRate1 = this.floatVal(this.accessRate1Target)
    const accessRate2 = this.floatVal(this.accessRate2Target)
    const accessFee = this.calculateUsageFee(accessCount, accessFreeLimit, accessThreshold, accessRate1, accessRate2)

    const smsCount = this.intVal(this.smsCountTarget)
    const smsRate = this.floatVal(this.smsRateTarget)
    const smsFee = smsCount * smsRate

    const totalUsageFee = emailFee + accessFee + smsFee
    this.usageFeeMonthlyTarget.textContent = this.formatCurrency(totalUsageFee)
    this.usageFeeAnnualTarget.textContent = this.formatCurrency(totalUsageFee * 12)

    let usageParts = []
    if (emailCount > 0) usageParts.push(`Emails (${emailCount.toLocaleString()}): ${currencySymbol()}${emailFee.toFixed(2)}`)
    if (accessCount > 0) usageParts.push(`Access (${accessCount.toLocaleString()}): ${currencySymbol()}${accessFee.toFixed(2)}`)
    if (smsCount > 0) usageParts.push(`SMS (${smsCount.toLocaleString()}): ${currencySymbol()}${smsFee.toFixed(2)}`)
    this.usageBreakdownTarget.innerHTML = usageParts.join("<br>") || "No usage entered"

    // Totals — annual is primary, monthly is annual ÷ 12
    const totalAnnual = baseFeeAnnual + (totalTxFee + golfnowFee + giftCardFee + totalUsageFee) * 12
    const totalMonthly = totalAnnual / 12
    const totalQuarterly = totalAnnual / 4

    this.totalAnnualTarget.textContent = this.formatCurrency(totalAnnual)
    this.totalMonthlyTarget.textContent = this.formatCurrency(totalMonthly)
    this.totalQuarterlyTarget.textContent = this.formatCurrency(totalQuarterly)

    if (members > 0) {
      this.effectivePerMemberTarget.textContent = `${currencySymbol()}${(totalAnnual / members / 12).toFixed(2)}/member/mo`
    } else {
      this.effectivePerMemberTarget.textContent = "—"
    }

    // ROI cost breakdown mirror
    this.roiCostBreakdownTarget.innerHTML =
      (breakdownParts.join("<br>") || "No members entered") +
      (minApplied ? `<br><span class="text-amber-700">Min ${currencySymbol()}${minimumFee.toFixed(0)}/mo applied</span>` : "")

    // ROI calculation
    const hoursPerWeek = parseFloat(this.hoursSliderTarget.value) || 0
    const hoursSaved   = hoursPerWeek * this.constructor.ADMIN_REDUCTION
    const valueAnnual  = hoursSaved * 52 * this.constructor.HOURLY_RATE

    if (totalAnnual > 0 && hoursPerWeek > 0) {
      this.roiHoursSavedTarget.textContent  = `${hoursSaved.toFixed(1)} hrs/wk`
      this.roiValueAnnualTarget.textContent = this.formatCurrency(valueAnnual)
      this.roiCostAnnualTarget.textContent  = this.formatCurrency(totalAnnual)
      this.roiMultipleTarget.textContent    = `${(valueAnnual / totalAnnual).toFixed(1)}× return`
    } else {
      this.roiHoursSavedTarget.textContent  = "—"
      this.roiValueAnnualTarget.textContent = "—"
      this.roiCostAnnualTarget.textContent  = totalAnnual > 0 ? this.formatCurrency(totalAnnual) : "—"
      this.roiMultipleTarget.textContent    = "—"
    }
  }

  syncHours() {
    this.hoursDisplayTarget.textContent = this.hoursSliderTarget.value
    this.calculate()
  }

  toggleRates() {
    this.ratesPanelTarget.classList.toggle("hidden")
  }

  calculateUsageFee(count, freeLimit, threshold, rate1, rate2) {
    if (count <= freeLimit) return 0
    const billable = count - freeLimit
    if (count <= threshold) {
      return billable * rate1
    }
    const standardBillable = threshold - freeLimit
    const highBillable = count - threshold
    return (standardBillable * rate1) + (highBillable * rate2)
  }

  intVal(target) {
    return parseInt(target.value) || 0
  }

  floatVal(target) {
    return parseFloat(target.value) || 0
  }

  formatCurrency(amount) {
    return `${currencySymbol()}${amount.toLocaleString("en-IE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
}
